export const WALLET_TELEMETRY_SCHEMA_VERSION = 1 as const

export const WALLET_TELEMETRY_EVENTS = [
	'connect',
	'wallet_sign',
	'wallet_error',
	'purchase_build',
	'purchase_submit',
	'listing_build',
	'listing_sign',
	'listing_authorize',
	// Offers (bids): buyer funds a bid, seller accepts/declines, buyer cancels,
	// owner sells into a collection/trait bid.
	'offer_make',
	'offer_accept',
	'offer_decline',
	'offer_cancel',
	'offer_fill',
	'utxo_split',
	// Social (Twetch): signing in with the wallet, publishing a post/thesis.
	'social_login',
	'social_post',
	'social_action',
	'client_crash'
] as const
export const WALLET_TELEMETRY_OFFER_EVENTS = [
	'offer_make',
	'offer_accept',
	'offer_decline',
	'offer_cancel',
	'offer_fill'
] as const
export const WALLET_TELEMETRY_OUTCOMES = [
	'started',
	'success',
	'failure',
	'cancelled',
	'stalled'
] as const
export const WALLET_TELEMETRY_STAGES = [
	'wallet_selected',
	'provider_probe',
	'request_accounts',
	'get_public_key',
	'connected',
	'sign_psbt',
	'sign_psbts',
	'sign_setup',
	'sign_purchase',
	'build_psbt',
	'submit_purchase',
	'authorize_listing',
	// Offer phases, in flow order.
	'build_offer',
	'sign_funding',
	'prepare_offer',
	'sign_offer',
	'activate_offer',
	'check_offer',
	'sign_accept',
	'submit_accept',
	'sign_refund',
	'submit_refund',
	'submit_decline',
	'broadcast',
	'sign_login',
	'sign_post',
	'publish_post',
	'sign_action',
	'send_action',
	'unknown'
] as const
export const WALLET_TELEMETRY_PROVIDERS = [
	'tap',
	'ordinalswallet',
	'unisat',
	'okx',
	'oyl',
	'xverse',
	'phantom',
	'leather',
	'dogelabs',
	'nintondo',
	'unknown'
] as const
export const WALLET_TELEMETRY_SURFACES = [
	'command_palette',
	'connect_modal',
	'tap_adapter',
	'collection_feed',
	'purchase_modal',
	'listing_flow',
	'offer_dialog',
	'offer_inbox',
	'instant_sell',
	'bids_page',
	'utxo_manager',
	'error_boundary',
	'wallet_core',
	'social_composer'
] as const
export const WALLET_TELEMETRY_ERROR_CATEGORIES = [
	'provider_missing',
	'method_missing',
	'user_rejected',
	'wallet_locked',
	'permission_denied',
	'timeout',
	'network',
	'insufficient_funds',
	'invalid_response',
	// Crash kinds (client_crash only): what broke, not just that it broke.
	'chunk_load',
	'render_loop',
	'hydration',
	'type_error',
	'reference_error',
	'syntax_error',
	'quota_exceeded',
	'security',
	'dom_mutation',
	// A texture/asset the browser could not decode; contained by a boundary.
	'asset_load',
	// The main thread stopped answering for seconds; no error was thrown.
	'long_task',
	'unknown'
] as const
export const WALLET_TELEMETRY_DURATION_BUCKETS = [
	'<250ms',
	'250ms-1s',
	'1-5s',
	'5-15s',
	'15s+'
] as const
export const WALLET_TELEMETRY_BROWSER_FAMILIES = [
	'chrome',
	'edge',
	'brave',
	'firefox',
	'safari',
	/** A wallet app's embedded browser (WKWebView / Android WebView). */
	'webview',
	'other'
] as const
export const WALLET_TELEMETRY_OS_FAMILIES = [
	'ios',
	'android',
	'macos',
	'windows',
	'linux',
	'other'
] as const
export const WALLET_TELEMETRY_ASSET_PROTOCOLS = [
	'tap',
	'rune',
	'brc20',
	'alkane',
	'ordinal',
	'unknown'
] as const
export const WALLET_TELEMETRY_ITEM_COUNT_BUCKETS = ['1', '2-5', '6-10', '11+'] as const
export const WALLET_TELEMETRY_OFFER_SCOPES = ['item', 'collection', 'trait'] as const
/** Which offers API the client talked to: the main marketplace API or the isolated preview. */
export const WALLET_TELEMETRY_OFFER_APIS = ['main', 'preview'] as const

export type WalletTelemetryEvent = (typeof WALLET_TELEMETRY_EVENTS)[number]
export type WalletTelemetryOutcome = (typeof WALLET_TELEMETRY_OUTCOMES)[number]
export type WalletTelemetryStage = (typeof WALLET_TELEMETRY_STAGES)[number]
export type WalletTelemetryProvider = (typeof WALLET_TELEMETRY_PROVIDERS)[number]
export type WalletTelemetrySurface = (typeof WALLET_TELEMETRY_SURFACES)[number]
export type WalletTelemetryErrorCategory = (typeof WALLET_TELEMETRY_ERROR_CATEGORIES)[number]
type WalletTelemetryDurationBucket = (typeof WALLET_TELEMETRY_DURATION_BUCKETS)[number]
type WalletTelemetryBrowserFamily = (typeof WALLET_TELEMETRY_BROWSER_FAMILIES)[number]
export type WalletTelemetryAssetProtocol = (typeof WALLET_TELEMETRY_ASSET_PROTOCOLS)[number]
type WalletTelemetryItemCountBucket = (typeof WALLET_TELEMETRY_ITEM_COUNT_BUCKETS)[number]

export interface WalletTelemetryPayload {
	schema_version: typeof WALLET_TELEMETRY_SCHEMA_VERSION
	telemetry: 'wallet_flow'
	flow_id: string
	attempt_id?: string
	device_family?: 'mobile' | 'desktop'
	event: WalletTelemetryEvent
	stage: WalletTelemetryStage
	outcome: WalletTelemetryOutcome
	provider: WalletTelemetryProvider
	surface: WalletTelemetrySurface
	network: 'BTC'
	duration_bucket?: WalletTelemetryDurationBucket
	provider_detected?: boolean
	api_capabilities?: number
	provider_version?: string
	browser_family?: WalletTelemetryBrowserFamily
	browser_major?: number
	/** Coarse OS from the user agent; never a full UA string. */
	os_family?: (typeof WALLET_TELEMETRY_OS_FAMILIES)[number]
	/** True when the page runs inside a wallet app's own webview (Xverse, UniSat, OKX…). */
	in_app_browser?: boolean
	error_category?: WalletTelemetryErrorCategory
	provider_error_code?: string
	collection_slug?: string
	asset_protocol?: WalletTelemetryAssetProtocol
	item_count_bucket?: WalletTelemetryItemCountBucket
	/** Snipe-protected (passthrough v4) purchase or listing. */
	protected?: boolean
	/** `client_crash` only: `Name: message`, ASCII, at most 120 chars. */
	crash_message?: string
	/** Route the crash happened on; long id-like segments collapsed to `:id`. */
	crash_path?: string
	/** First Next.js chunk frame of the stack (`chunks/.../x.js:line:col`). */
	crash_frame?: string
	/** Top of the React component stack: `Name@chunks/.../x.js:line:col > …`. */
	crash_components?: string
	/** What the user last clicked or typed into, and how long before the crash. */
	crash_action?: string
	/**
	 * `client_crash` only: the overlays open at the moment of the crash
	 * (dialogs, toasts, popovers) by role and label, so a render loop reads
	 * as "purchase modal + success toast" instead of a bare React error.
	 */
	crash_dom?: string
	/**
	 * Failures only: the phases this attempt went through before failing,
	 * oldest first, e.g. `build+ > sign-(wallet_account_mismatch)`; `std:`
	 * marks a standard-listing fallback phase.
	 */
	trail?: string
	/**
	 * The buyer's payment address on a failed purchase build/submit, so an
	 * `insufficient_funds` can be checked against the wallet's real UTXOs.
	 * Public on-chain data; only ever attached to failures. Offer events send
	 * it shortened (first five and last four characters).
	 */
	buyer_address?: string
	/** The seller's ordinals address on a failed listing phase; shortened on offer events. */
	seller_address?: string
	/**
	 * Bounded, sanitized text of the underlying error (API message, detail
	 * and ref, or the thrown message) with ids and addresses collapsed.
	 */
	error_detail?: string
	/** Wallet balance the client knew of when a purchase failed, in sats. */
	balance_sats?: number
	/** Price of the items being bought when a purchase failed, in sats; offer events send its order of magnitude. */
	price_sats?: number

	/** Fee rate the build used, in sat/vB. */
	fee_rate?: number
	/** Route the failure happened on; long id-like segments collapsed to `:id`. */
	path?: string
	/** Offer events only: item, collection or trait bid. */
	offer_scope?: (typeof WALLET_TELEMETRY_OFFER_SCOPES)[number]
	/** Offer events only: main marketplace API or the isolated offers preview. */
	offer_api?: (typeof WALLET_TELEMETRY_OFFER_APIS)[number]
	/**
	 * Offer events only: the first 8 hex characters of the offer id, which
	 * the API's own `[offers]` alert carries too, so one failure reads as one
	 * thread across client and server. Not an address, not a txid.
	 */
	offer_ref?: string
	/** Git SHA of the build the page was served from (first 12 chars). */
	app_build?: string
	/**
	 * The page was machine-translated in the browser (Google Translate and
	 * friends rewrite text nodes React owns, which ends in removeChild /
	 * insertBefore NotFoundErrors).
	 */
	page_translated?: boolean
}
